import { createContext, use, useEffect, useMemo } from 'react';
import Cookies from 'js-cookie';
import AppConfig from '../AppConfig';
import { JWTPayload, decodeAuthToken, redirectToPlumApp, track } from '@/utils';
import { IUserConfig } from '@/types';
import { useQuery } from '@tanstack/react-query';
import { authenticateMeQuery } from '@/queries/auth.queries';
import { EUserRole } from '@/constants';

const AppContext = createContext<{
  isWebView: boolean;
  xPlumToken: string | null;
  decodedToken: JWTPayload | null;
  isAppLoading: boolean;
  userConfig: IUserConfig | null;
  userFirstName: string | null;
  entrySurface: string | null;
  gender: string | null;
  dateOfBirth: string | null;
  userRole: EUserRole;
}>({
  isWebView: false,
  xPlumToken: null,
  decodedToken: null,
  isAppLoading: false,
  userConfig: null,
  userFirstName: null,
  entrySurface: null,
  gender: null,
  dateOfBirth: null,
  userRole: EUserRole.USER,
});
const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const searchParams = new URLSearchParams(window.location.search);
  const isWebViewParam = searchParams.get('webview') === 'true';
  /**
   * Will always be true for triage
   */

  useEffect(() => {
    if (isWebViewParam) {
      Cookies.set('isWebView', 'true');
    }
  }, [isWebViewParam]);

  const isWebView = useMemo(() => {
    return isWebViewParam || Cookies.get('isWebView') === 'true';
  }, [isWebViewParam]);

  const xPlumToken = useMemo(() => {
    return Cookies.get(AppConfig.JWT_TOKEN_COOKIE_NAME) ?? null;
  }, []);

  const xPlumIdentityToken = useMemo(() => {
    return Cookies.get(AppConfig.IDENTITY_TOKEN_COOKIE_NAME) ?? null;
  }, []);

  const userFirstName = useMemo(() => {
    return Cookies.get(AppConfig.USER_FIRST_NAME_COOKIE_NAME) ?? null;
  }, []);

  const entrySurface = useMemo(() => {
    return Cookies.get(AppConfig.ENTRY_SURFACE_COOKIE_NAME) ?? 'web';
  }, []);

  const gender = useMemo(() => {
    return Cookies.get(AppConfig.GENDER_COOKIE_NAME) ?? null;
  }, []);

  const dateOfBirth = useMemo(() => {
    return Cookies.get(AppConfig.DATE_OF_BIRTH_COOKIE_NAME) ?? null;
  }, []);

  const { isLoading, isFetched, data } = useQuery({
    queryKey: authenticateMeQuery().queryKey,
    queryFn: authenticateMeQuery().queryFn,
    refetchInterval: 1000 * 60 * 15, // 15 minutes,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
  });

  // Determine user role based on identity token or decoded JWT source
  const userRole = useMemo(() => {
    if (xPlumIdentityToken) return EUserRole.DOCTOR;
    const decoded = decodeAuthToken(xPlumToken ?? '');
    if (decoded?.source === 'IAM') return EUserRole.DOCTOR;
    return EUserRole.USER;
  }, [xPlumIdentityToken, xPlumToken]);

  useEffect(() => {
    window.dev?.i('AppProvider', 'isFetched', { isFetched, xPlumToken });
    if (isFetched && !xPlumToken && !xPlumIdentityToken) {
      const redirectToUrl = encodeURI(window.location.href);
      if (isWebView && window.NativeBridge?.onNativeLogout) {
        window.NativeBridge.onNativeLogout();
        return;
      } else {
        redirectToPlumApp(`/logout?redirectTo=${redirectToUrl}`)();
      }
    }
  }, [isFetched, xPlumToken, isWebView, xPlumIdentityToken]);

  // Handle user role validation and redirect logic
  useEffect(() => {
    if (isFetched && userRole === EUserRole.USER) {
      // For users, isWebView must be true
      if (!isWebView) {
        const redirectToUrl = encodeURI(window.location.href);
        if (window.NativeBridge?.onNativeLogout) {
          window.NativeBridge.onNativeLogout();
        } else {
          redirectToPlumApp(`/logout?redirectTo=${redirectToUrl}`)();
        }
      }
    }
  }, [isFetched, userRole, isWebView]);

  useEffect(() => {
    const decodedToken = decodeAuthToken(xPlumToken ?? '');
    if (decodedToken) {
      const handleAnalyticsLoaded = () => {
        track.identifyUserToAnalytics(decodedToken);
      };

      window.addEventListener(
        AppConfig.ANALYTICS_LOADED_EVENT,
        handleAnalyticsLoaded,
      );

      return () => {
        window.removeEventListener(
          AppConfig.ANALYTICS_LOADED_EVENT,
          handleAnalyticsLoaded,
        );
      };
    }
  }, [xPlumToken]);

  const isAppLoading = isLoading || !isFetched;

  useEffect(() => {
    if (data?.id && window?.smartlook) {
      window?.smartlook?.('init', AppConfig.SMARTLOOK_KEY, {
        region: 'eu',
      });
      window?.smartlook('identify', data?.id as string, {
        memberId: data?.id,
        organisationId: data?.organisation?.id,
        email: data?.email,
      });
    }
  }, [data]);

  const values = useMemo(() => {
    const decodedToken = decodeAuthToken(xPlumToken ?? '');

    return {
      isWebView,
      xPlumToken: xPlumToken ? xPlumToken : xPlumIdentityToken,
      decodedToken,
      isAppLoading,
      userConfig: data as IUserConfig,
      userFirstName,
      entrySurface,
      gender,
      dateOfBirth,
      userRole,
    };
  }, [
    isWebView,
    xPlumToken,
    data,
    isAppLoading,
    userFirstName,
    entrySurface,
    gender,
    dateOfBirth,
    xPlumIdentityToken,
    userRole,
  ]);

  return <AppContext.Provider value={values}>{children}</AppContext.Provider>;
};

export { AppContext };
export default AppProvider;
